import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../../context/CartContext";
import { useToast } from "../../context/ToastContext";
import { capQuantity, readAvailability } from "../../Utils/availability";
import { formatLek } from "../../Utils/currency";

export default function BasketPurchase({ basket, farm }) {
  const { t } = useTranslation();
  const { cartItems = [], addItemToCart } = useCart();
  const { success } = useToast();
  const [requested, setRequested] = useState(1);
  const available = readAvailability(basket);
  const inCart = Number(cartItems.find((item) => String(item.id) === String(basket.id))?.quantity) || 0;
  const remaining = available === null ? null : Math.max(0, available - inCart);
  const quantity = capQuantity(requested, remaining);
  const soldOut = available === 0;

  const add = () => {
    if (quantity === 0) return;
    const added = addItemToCart({
      id: basket.id,
      farmId: basket.farmId,
      farmName: farm?.name || "",
      name: basket.name,
      price: basket.price,
      image: basket.image || "",
      availableQuantity: basket.availableQuantity,
      quantity,
    });
    if (added) {
      success(`${basket.name} ${t("cart.added", { defaultValue: "added to cart" })}`);
      setRequested(1);
    }
  };

  return (
    <>
      <p className="bp-description">
        {t("baskets.fixed_contents", { defaultValue: "The contents shown are for one basket. Each additional basket contains the same quantities." })}
      </p>
      {inCart > 0 && (
        <p className="bp-cart-note" role="status">
          {t("baskets_page.in_cart", { count: inCart, defaultValue: "In your cart: {{count}}" })}{" "}
          <Link to="/cart">{t("baskets.view_cart", { defaultValue: "View cart" })}</Link>
        </p>
      )}
      <div className="bp-selection-total" role="status" aria-live="polite" aria-atomic="true">
        <span>{t("baskets.selection_total", { count: quantity, defaultValue: "Basket subtotal ({{count}})" })}</span>
        <strong>{formatLek(basket.price * quantity)}</strong>
      </div>
      <div className="bp-actions">
        <div className="bp-quantity" role="group" aria-label={t("baskets.quantity", { defaultValue: "Number of baskets" })}>
          <button type="button" onClick={() => setRequested(Math.max(1, quantity - 1))} disabled={quantity <= 1}
            aria-label={t("baskets.decrease", { defaultValue: "Decrease quantity" })}>–</button>
          <span>{quantity}</span>
          <button type="button" onClick={() => setRequested(quantity + 1)} disabled={remaining !== null && quantity >= remaining}
            aria-label={t("baskets.increase", { defaultValue: "Increase quantity" })}>+</button>
        </div>
        <button type="button" className="bp-add-to-cart" onClick={add} disabled={quantity === 0}>
          {soldOut
            ? t("baskets.sold_out", { defaultValue: "Sold out" })
            : quantity === 0
              ? t("baskets_page.cart_limit", { defaultValue: "Limit reached" })
              : t("baskets.add_to_cart", { defaultValue: "Add to Cart" })}
        </button>
      </div>
      {!soldOut && remaining === 0 && (
        <p className="bp-availability" role="status">
          {t("baskets.all_in_cart", { defaultValue: "All available baskets of this type are already in your cart." })}
        </p>
      )}
    </>
  );
}
